/**
 * AdminHousingV2 — Phase H3 opt-in page.
 *
 * Consumes the 4 additive aggregated endpoints introduced in Phase H2:
 *   GET /api/housing/aggregated/inventory
 *   GET /api/housing/aggregated/allocations
 *   GET /api/housing/aggregated/operations
 *   GET /api/housing/aggregated/maintenance
 *
 * Deliberately does NOT touch the legacy `AdminUnifiedHousing.jsx` /
 * `HousingAssignment.jsx` / `HousingManager.jsx` pages — those remain
 * live so daily admin flows keep working. Once ops staff prefer this
 * page for a sprint, Phase H4 retires the legacy routes.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Building2, BedDouble, ClipboardList, Wrench, RefreshCw, AlertTriangle,
  ShieldCheck, PowerOff, Rocket,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });


export default function AdminHousingV2() {
  const [h4, setH4] = useState(null);

  // Iter-435c: signal the H4 dogfood gate. Best-effort, silent failure.
  useEffect(() => {
    axios.post(`${API}/api/fwtv/admin/housing-v2/track-visit`, {}, { headers: authH() })
      .then(r => setH4(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-4">
        <header className="flex items-center gap-3">
          <Building2 className="h-7 w-7 text-indigo-400" />
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-white">Housing (v2)</h1>
            <p className="text-sm text-slate-400">
              Single-page housing dashboard powered by the Phase-H2 aggregated endpoints.
              Legacy admin pages remain live under the old menu — this is an opt-in preview.
            </p>
          </div>
        </header>

        <H4MigrationCard tracker={h4} onChange={setH4} />

        <Tabs defaultValue="inventory">
          <TabsList className="bg-slate-900 border border-slate-800" data-testid="hv2-tabs">
            <TabsTrigger value="inventory" data-testid="hv2-tab-inventory">
              <BedDouble className="h-4 w-4 mr-1" /> Inventory
            </TabsTrigger>
            <TabsTrigger value="allocations" data-testid="hv2-tab-allocations">
              <ClipboardList className="h-4 w-4 mr-1" /> Allocations
            </TabsTrigger>
            <TabsTrigger value="operations" data-testid="hv2-tab-operations">
              <Building2 className="h-4 w-4 mr-1" /> Operations
            </TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="hv2-tab-maintenance">
              <Wrench className="h-4 w-4 mr-1" /> Maintenance
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inventory"   className="mt-4"><InventoryTab /></TabsContent>
          <TabsContent value="allocations" className="mt-4"><AllocationsTab /></TabsContent>
          <TabsContent value="operations"  className="mt-4"><OperationsTab /></TabsContent>
          <TabsContent value="maintenance" className="mt-4"><MaintenanceTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}


function useFetch(path) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}${path}`, { headers: authH() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to load ${path}`);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return { data, busy, reload: load };
}


function KPI({ label, value, tone = 'default' }) {
  const toneCls = {
    default:  'text-white',
    good:     'text-emerald-300',
    warn:     'text-amber-300',
    danger:   'text-rose-300',
    accent:   'text-indigo-300',
  }[tone];
  return (
    <Card className="bg-slate-900/60 border-slate-800">
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`text-3xl font-semibold mt-1 ${toneCls}`}>{value ?? '—'}</div>
      </CardContent>
    </Card>
  );
}


function TabHeader({ title, busy, reload, testId }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="text-sm text-slate-300">{title}</div>
      <Button size="sm" variant="outline" onClick={reload}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid={testId}>
        <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} />Refresh
      </Button>
    </div>
  );
}


// -------- Inventory --------
function InventoryTab() {
  const { data, busy, reload } = useFetch('/api/housing/aggregated/inventory');
  const s = data?.rooms_by_status;
  return (
    <div data-testid="hv2-inventory-panel">
      <TabHeader title="Blocks + rooms by status + capacity" busy={busy} reload={reload}
                    testId="hv2-inventory-refresh" />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Total rooms"   value={s?.total} />
        <KPI label="Available"     value={s?.available}     tone="good" />
        <KPI label="Occupied"      value={s?.occupied}      tone="accent" />
        <KPI label="Maintenance"   value={s?.maintenance}   tone="warn" />
        <KPI label="Capacity"      value={s?.capacity_persons} />
      </div>
      <Card className="bg-slate-900/60 border-slate-800 mt-4">
        <CardHeader><CardTitle className="text-sm">Blocks ({data?.blocks?.length || 0})</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-xs">
          {!data?.blocks?.length && <p className="text-slate-500 py-4 text-center">No blocks yet.</p>}
          {(data?.blocks || []).map(b => (
            <div key={b.id} className="border border-slate-800 rounded p-2 flex items-center justify-between">
              <span className="text-slate-100">{b.name || '(unnamed)'}</span>
              <span className="text-slate-500">{b.tenant_id || '—'}</span>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="bg-slate-900/60 border-slate-800 mt-4">
        <CardHeader><CardTitle className="text-sm">Sample rooms ({data?.sample_rooms?.length || 0})</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
          {(data?.sample_rooms || []).slice(0, 30).map(r => (
            <div key={r.id} className="border border-slate-800 rounded p-2 bg-slate-800/30">
              <div className="text-slate-100 font-medium">Room {r.room_number || '?'}</div>
              <div className="text-slate-500 mt-0.5">
                Floor {r.floor ?? '—'} · capacity {r.capacity ?? '—'}
              </div>
              <Badge variant="outline" className="text-[10px] mt-1">
                {r.status || 'available'}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}


// -------- Allocations --------
function AllocationsTab() {
  const { data, busy, reload } = useFetch('/api/housing/aggregated/allocations?limit=100');
  return (
    <div data-testid="hv2-allocations-panel">
      <TabHeader title="Bookings + per-event breakdown" busy={busy} reload={reload}
                    testId="hv2-allocations-refresh" />
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader><CardTitle className="text-sm">By event</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-xs">
          {!data?.by_event?.length && <p className="text-slate-500 py-4 text-center">No active allocations.</p>}
          {(data?.by_event || []).map(e => (
            <div key={e.event_id || 'null'} className="border border-slate-800 rounded p-2 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="text-slate-100 truncate">{e.event_name || '(unassigned)'}</div>
                <div className="text-slate-500 text-[10px] truncate">{e.event_id || '—'}</div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-300">
                  {e.allocations} allocated
                </Badge>
                <Badge variant="outline" className="text-[10px] border-emerald-400/40 text-emerald-300">
                  {e.checked_in} checked-in
                </Badge>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="bg-slate-900/60 border-slate-800 mt-4">
        <CardHeader><CardTitle className="text-sm">Recent allocations ({data?.rows?.length || 0})</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-xs">
          {(data?.rows || []).slice(0, 25).map(r => (
            <div key={r.id} className="border border-slate-800 rounded p-2 flex justify-between">
              <div className="min-w-0 flex-1">
                <div className="text-slate-100 truncate">
                  Delegate {r.delegate_id?.slice(-6) || '—'} · Room {r.room?.room_number || '?'}
                </div>
                <div className="text-slate-500 text-[10px]">{r.status || 'unknown'}</div>
              </div>
              <Badge variant="outline" className="text-[10px]">
                {r.status}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}


// -------- Operations --------
function OperationsTab() {
  const { data, busy, reload } = useFetch('/api/housing/aggregated/operations');
  return (
    <div data-testid="hv2-operations-panel">
      <TabHeader title="Live snapshot of check-ins / departures" busy={busy} reload={reload}
                    testId="hv2-operations-refresh" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <KPI label="Active stays"      value={data?.active_stays}      tone="accent" />
        <KPI label="Pending arrivals"  value={data?.pending_arrivals}  tone="warn" />
        <KPI label="Departures 24h"    value={data?.departures_24h}    tone="default" />
      </div>
      <Card className="bg-slate-900/60 border-slate-800 mt-4">
        <CardHeader><CardTitle className="text-sm">Recent check-ins ({data?.recent_checkins?.length || 0})</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-xs">
          {!data?.recent_checkins?.length && (
            <p className="text-slate-500 py-4 text-center">No recent check-ins.</p>
          )}
          {(data?.recent_checkins || []).map(r => (
            <div key={r.id} className="border border-slate-800 rounded p-2 flex justify-between">
              <div className="min-w-0 flex-1">
                <div className="text-slate-100 truncate">
                  Target <code>{r.target_id?.slice(-6)}</code>
                </div>
                <div className="text-slate-500 text-[10px]">
                  {r.verdict} · {r.reason} · {r.scanned_at ? new Date(r.scanned_at).toLocaleTimeString() : '—'}
                </div>
              </div>
              <Badge variant="outline" className={`text-[10px] ${
                r.verdict === 'green' ? 'border-emerald-400/40 text-emerald-300'
                  : r.verdict === 'amber' ? 'border-amber-400/40 text-amber-300'
                  : 'border-rose-400/40 text-rose-300'
              }`}>{r.verdict}</Badge>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}


// -------- Maintenance --------
function MaintenanceTab() {
  const { data, busy, reload } = useFetch('/api/housing/aggregated/maintenance');
  return (
    <div data-testid="hv2-maintenance-panel">
      <TabHeader title="Open maintenance + cleaning" busy={busy} reload={reload}
                    testId="hv2-maintenance-refresh" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <KPI label="Open maintenance" value={data?.open_maintenance} tone="warn" />
        <KPI label="Open cleaning"    value={data?.open_cleaning}    tone="accent" />
        <KPI label="Urgent"           value={data?.urgent_maintenance} tone="danger" />
      </div>
      {data?.urgent_maintenance > 0 && (
        <div className="mt-3 border border-rose-400/40 bg-rose-500/10 rounded p-3 text-xs text-rose-200 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" />
          {data.urgent_maintenance} urgent ticket(s) — action required.
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader><CardTitle className="text-sm">Recent maintenance</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-xs">
            {!data?.recent_maintenance?.length && <p className="text-slate-500 py-3 text-center">Nothing open.</p>}
            {(data?.recent_maintenance || []).map(t => (
              <div key={t.id} className="border border-slate-800 rounded p-2">
                <div className="text-slate-100 truncate">{t.summary || t.description || 'Ticket'}</div>
                <div className="text-slate-500 text-[10px]">{t.status} · priority {t.priority || 'normal'}</div>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader><CardTitle className="text-sm">Recent cleaning</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-xs">
            {!data?.recent_cleaning?.length && <p className="text-slate-500 py-3 text-center">Nothing scheduled.</p>}
            {(data?.recent_cleaning || []).map(t => (
              <div key={t.id} className="border border-slate-800 rounded p-2">
                <div className="text-slate-100 truncate">
                  Room {t.room_id?.slice(-6) || '?'} · {t.status || 'requested'}
                </div>
                <div className="text-slate-500 text-[10px]">
                  {t.scheduled_at ? new Date(t.scheduled_at).toLocaleString() : '—'}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}



// -------- H4 Migration widget --------
function H4MigrationCard({ tracker, onChange }) {
  const [status, setStatus] = useState(tracker);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (tracker) setStatus(tracker);
  }, [tracker]);

  const load = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/fwtv/admin/housing-v2/h4-status`,
                                    { headers: authH() });
      setStatus(r.data);
      onChange?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load H4 status');
    } finally { setBusy(false); }
  };

  const flip = async (enable, force = false) => {
    setBusy(true);
    try {
      const path = enable ? 'enable-h4' : 'disable-h4';
      const r = await axios.post(`${API}/api/fwtv/admin/housing-v2/${path}`,
                                     enable ? { force } : {},
                                     { headers: authH() });
      setStatus(r.data);
      onChange?.(r.data);
      toast.success(enable ? '301 redirects active' : 'Legacy routes restored');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : detail?.hint || 'H4 action failed';
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (!status) return null;

  const totalPct = Math.min(100, Math.round((status.total_visits / 50) * 100));
  const adminsPct = Math.min(100, Math.round((status.unique_admins_7d / 3) * 100));

  return (
    <Card className="bg-slate-900/60 border-slate-800" data-testid="h4-migration-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          {status.h4_enabled ? (
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
          ) : (
            <Rocket className="h-5 w-5 text-indigo-400" />
          )}
          <CardTitle className="text-sm">Phase H4 — legacy route retirement</CardTitle>
          {status.h4_enabled && (
            <Badge variant="outline"
                      className="border-emerald-400/40 text-emerald-300 bg-emerald-500/10 text-[10px]"
                      data-testid="h4-status-badge">
              301 redirects active{status.forced ? ' (forced)' : ''}
            </Badge>
          )}
          {!status.h4_enabled && status.ready_for_h4 && (
            <Badge variant="outline"
                      className="border-indigo-400/40 text-indigo-300 bg-indigo-500/10 text-[10px]"
                      data-testid="h4-status-badge">
              Ready to migrate
            </Badge>
          )}
          {!status.h4_enabled && !status.ready_for_h4 && (
            <Badge variant="outline"
                      className="border-amber-400/40 text-amber-300 bg-amber-500/10 text-[10px]"
                      data-testid="h4-status-badge">
              Gate not met
            </Badge>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={load}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="h4-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${busy ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid md:grid-cols-2 gap-3 text-xs">
          <ProgressBar
            label={`Distinct admins (7d) · ${status.unique_admins_7d} / 3`}
            pct={adminsPct}
            good={status.unique_admins_7d >= 3}
            testId="h4-progress-admins"
          />
          <ProgressBar
            label={`Total visits · ${status.total_visits} / 50`}
            pct={totalPct}
            good={status.total_visits >= 50}
            testId="h4-progress-visits"
          />
        </div>
        <p className="text-[11px] text-slate-500">
          When enabled, GETs on <code>/api/housing/&#123;overview,blocks,rooms,allocations&#125;</code>{' '}
          301-redirect to the Phase-H2 aggregated endpoints. Writes (link, buildings, checkin) pass through untouched.
        </p>
        <div className="flex flex-wrap gap-2">
          {!status.h4_enabled && (
            <>
              <Button size="sm" onClick={() => flip(true, false)}
                        disabled={busy || !status.ready_for_h4}
                        data-testid="h4-enable-btn">
                <Rocket className="h-3 w-3 mr-1" />Enable 301 redirects
              </Button>
              {!status.ready_for_h4 && (
                <Button size="sm" variant="outline" onClick={() => flip(true, true)}
                          disabled={busy}
                          className="border-amber-400/40 text-amber-300 hover:bg-amber-500/10"
                          data-testid="h4-force-btn">
                  Force enable (super-admin override)
                </Button>
              )}
            </>
          )}
          {status.h4_enabled && (
            <Button size="sm" variant="outline" onClick={() => flip(false)}
                      disabled={busy}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      data-testid="h4-disable-btn">
              <PowerOff className="h-3 w-3 mr-1" />Disable (restore legacy routes)
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


function ProgressBar({ label, pct, good, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-slate-400">
        <span>{label}</span>
        <span className={good ? 'text-emerald-300' : 'text-slate-500'}>{pct}%</span>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full mt-1 overflow-hidden">
        <div
          className={`h-full ${good ? 'bg-emerald-400' : 'bg-indigo-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
